"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import type { HeroSlide } from "@/app/lib/home/data";

const SLIDE_INTERVAL_MS = 7000;

// Crossfading hero background. Only the first slide is the LCP image
// (priority + <picture> with the mobile art-direction variant); the rest
// load lazily and fade in over it.
export default function HeroBackground({ slides }: { slides: HeroSlide[] }) {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }
    const interval = setInterval(() => {
      setActiveIndex((current) => (current + 1) % slides.length);
    }, SLIDE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [slides.length]);

  return (
    <div className="absolute inset-0 z-10 bg-black" aria-hidden="true">
      {slides.map((slide, index) => (
        <div
          key={slide.desktop}
          className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
            index === activeIndex ? "opacity-100" : "opacity-0"
          }`}
        >
          {index === 0 ? (
            <picture>
              <source media="(max-width: 768px)" srcSet={slide.mobile} />
              <img
                src={slide.desktop}
                alt={slide.alt}
                fetchPriority="high"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </picture>
          ) : (
            <Image
              src={slide.desktop}
              alt={slide.alt}
              fill
              sizes="100vw"
              loading="lazy"
              className="object-cover"
            />
          )}
        </div>
      ))}
      {/* Legibility overlay */}
      <div className="absolute inset-0 bg-black/45 bg-gradient-to-b from-black/30 via-black/40 to-black/70"></div>
    </div>
  );
}
